// ─── Workshop Truck Availability Tracker ───
// RepairCostReport view – Shows trucks with required parts and their aggregated costs
// IASL Technical Academy / Lafarge Driving Institute, Nigeria

import { useState, useMemo } from 'react'
import { Download, Search, SearchX } from 'lucide-react'
import type { ToastMessage, RequiredPart } from '@/types'
import { useTrucks } from '@/hooks/useTrucks'

interface RepairCostReportProps {
    addToast: (type: ToastMessage['type'], message: string) => void
}

const KNOWN_CLIENTS = ['LAPW', 'TSL', 'LAPE', 'TNP']

export default function RepairCostReport({ addToast }: RepairCostReportProps) {
    const { trucks, isLoading } = useTrucks()
    const [apiClient, setApiClient] = useState<string>('')
    const [searchInput, setSearchInput] = useState('')

    // Compute detailed costs for each truck
    const processedData = useMemo(() => {
        let result = trucks.map(truck => {
            let totalCost = 0
            let partsCount = 0

            if (truck.required_parts) {
                try {
                    const parsed = JSON.parse(truck.required_parts) as Record<string, RequiredPart[]>
                    Object.values(parsed).forEach(items => {
                        partsCount += items.length
                        items.forEach(item => {
                            totalCost += (item.estimated_cost || 0)
                        })
                    })
                } catch (e) {
                    // ignore parsing error
                }
            }

            return { ...truck, totalCost, partsCount }
        })

        // Filter out trucks with 0 cost if we want a cost report
        result = result.filter(t => t.totalCost > 0)

        // filter by client
        if (apiClient) {
            result = result.filter(t => t.client_name === apiClient)
        }

        // filter by search
        if (searchInput.trim()) {
            const q = searchInput.toLowerCase()
            result = result.filter(
                (t) =>
                    t.registration?.toLowerCase().includes(q) ||
                    t.asset_id?.toLowerCase().includes(q)
            )
        }

        // sorting by cost descending
        result.sort((a, b) => b.totalCost - a.totalCost)

        return result
    }, [trucks, apiClient, searchInput])

    const grandTotal = useMemo(() => {
        return processedData.reduce((acc, curr) => acc + curr.totalCost, 0)
    }, [processedData])

    const handleExportCSV = () => {
        if (processedData.length === 0) return

        const headers = ['Fleet #', 'Client', 'Parts Count', 'Total Estimated Cost (NGN)']
        const rows = processedData.map(t => [
            t.registration,
            t.client_name,
            t.partsCount,
            t.totalCost
        ])

        const csvContent = [headers, ...rows].map(e => e.join(',')).join('\\n')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `repair_cost_report_${new Date().toISOString().split('T')[0]}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        addToast('success', 'Exported CSV successfully')
    }

    // ── Any filter active? ────────────────────────────────────
    const hasActiveFilters = !!(searchInput || apiClient)

    const clearFilters = () => {
        setSearchInput('')
        setApiClient('')
    }

    return (
        <div>
            {/* ═══ PAGE HEADER & SUMMARY ═════════════════════════════════════ */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Repair Cost Report</h1>
                    <p className="text-sm text-gray-500">
                        Estimated costs of required parts across the fleet
                    </p>
                </div>

                {/* Total Summary Box */}
                <div className="bg-white border-l-4 border-blue-600 shadow-sm rounded-r-xl px-6 py-3 flex items-center justify-between min-w-[280px]">
                    <div>
                        <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Total Est. Cost</p>
                        <p className="text-2xl font-bold text-gray-900 mt-0.5">
                            <span className="text-gray-400 font-medium text-lg mr-1">₦</span>
                            {grandTotal.toLocaleString()}
                        </p>
                    </div>
                    <button
                        onClick={handleExportCSV}
                        disabled={processedData.length === 0}
                        className="flex flex-col items-center justify-center p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Download size={20} className="mb-1" />
                        <span className="text-[10px] font-semibold uppercase tracking-wider">Export</span>
                    </button>
                </div>
            </div>

            {/* ═══ FILTER BAR ══════════════════════════════════════ */}
            <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
                <div className="flex gap-3 flex-wrap items-center">
                    {/* Search */}
                    <div className="relative flex-1 min-w-[240px]">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search fleet #..."
                            className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* Client filter */}
                    <select
                        value={apiClient}
                        onChange={(e) => setApiClient(e.target.value)}
                        className="min-w-[160px] border border-gray-300 rounded-lg px-3 py-2.5 text-sm
                       focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="">All Clients</option>
                        {KNOWN_CLIENTS.map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>

                    {/* Clear filters */}
                    {hasActiveFilters && (
                        <button
                            onClick={clearFilters}
                            className="flex items-center gap-1 text-gray-500 hover:text-red-500 text-sm
                         transition-colors px-2 py-2"
                        >
                            Clear
                        </button>
                    )}
                </div>
            </div>

            {/* ═══ TABLE ═══════════════════════════════════════════ */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 w-10">
                                    #
                                </th>
                                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                                    Fleet #
                                </th>
                                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                                    Client
                                </th>
                                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                                    Parts Count
                                </th>
                                <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">
                                    Total Cost (₦)
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {/* LOADING STATE */}
                            {isLoading && (
                                <>
                                    {Array.from({ length: 5 }, (_, i) => (
                                        <tr key={`skel-${i}`} className="border-b border-gray-100 animate-pulse">
                                            <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-6" /></td>
                                            <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-24" /></td>
                                            <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-16" /></td>
                                            <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-8 mx-auto" /></td>
                                            <td className="px-4 py-3 flex justify-end"><div className="h-4 bg-gray-200 rounded w-24" /></td>
                                        </tr>
                                    ))}
                                </>
                            )}

                            {/* EMPTY STATE */}
                            {!isLoading && processedData.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="py-16 text-center">
                                        <SearchX size={48} className="mx-auto text-gray-300 mb-4" />
                                        <p className="text-gray-600 font-medium mb-1">
                                            No repair costs found for the selected criteria
                                        </p>
                                    </td>
                                </tr>
                            )}

                            {/* DATA ROWS */}
                            {!isLoading && processedData.map((truck, idx) => (
                                <tr
                                    key={truck.asset_id}
                                    className="hover:bg-gray-50 border-b border-gray-100 transition-colors"
                                >
                                    <td className="px-4 py-3 text-sm text-gray-400">
                                        {idx + 1}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-semibold text-gray-800">
                                        {truck.registration}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700">
                                            {truck.client_name}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-center text-sm font-medium text-gray-600">
                                        {truck.partsCount}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <span className="font-mono font-bold text-gray-900">
                                            {truck.totalCost.toLocaleString()}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}
